import {
  Box,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import classNames from 'classnames'
import React, { ReactElement } from 'react'
import {
  DateField,
  EditButton,
  FunctionField,
  ReduxState,
  ReferenceField,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TabbedShowLayoutTabs,
  TextField,
  TextInput,
  TitleProps,
  sanitizeFetchType,
  useQuery,
  useTranslate,
} from 'react-admin'
import { useSelector } from 'react-redux'
import { Authority } from '../../../core/auth/Authority'
import { ToggleType } from '../../../core/devices/engines/engine-lock-button'
import { EngineDto } from '../../../core/dto/device/engine/engine.dto'
import { ResourceName } from '../../../core/ResourceName'
import RelationTab from '../../common/RelationTab'
import relationTabFilter from '../../common/RelationTabFilter'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { DeviceStatusesTab } from '../common/modules/ModuleStatus'
import { EngineForceReportStateButton } from './engine-show/EngineForceReportStateButton'
import { EngineLockButton } from './engine-show/EngineLockButton'
import { EngineStandbyModeButton } from './engine-show/EngineStandbyModeButton'
import { EngineResetModulesButton } from './engine-show/EngineResetModulesButton'
import { HardwareVersions } from '../../../core/enum/HardwareVersions'
import { SPOM4FullAddressField } from '../passages/SPOM4Address'
import { CameraDto } from '../../../core/dto/device/camera/cameras/camera.dto'
import { VerifierDeviceDisabledDto } from '../../../core/dto/device/verifier-device-disabled/verifier-device-disabled.dto'
import { DeviceCategoryExtendedDto } from '../../../core/dto/device-category/device-category-extended.dto'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { EngineUploadPackButton } from './engine-show/EngineUploadPackButton'
import { ButtonWithRecurringJob } from '../../common/ButtonWithRecurringJob'
import { RecurringMethodButton } from '../../common/RecurringMethodButton'
import { BlankActions } from '../../common/BlankActions'

const useStyles = makeStyles((theme: any) =>
  createStyles({
    show: {
      width: `calc(100vw - ${theme?.sidebar?.closedWidth}px - 34px)`,
    },
    showSideOpen: {
      maxWidth: `calc(100vw - ${theme?.sidebar?.width}px - 34px)`,
    },
    showXSmall: {
      width: '100vw',
    },
  }),
)

interface EngineTitleProps extends TitleProps {
  record?: EngineDto
}

const ShowTitle = (data: EngineTitleProps) => {
  const { record: engine } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.engines.titles.show')}: {engine?.id}. {engine?.name}
    </span>
  )
}

const GeneralActions = ({ ...props }) => {
  const { record: engine } = props
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1}>
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={<EngineStandbyModeButton />}
              recurringAction={<EngineStandbyModeButton asRecurringJob />}
              disabled={
                !hasAuthority(Authority.ENGINES_SET_STANDBY_MODE_BUTTON)
              }
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              disabled={!hasAuthority(Authority.ENGINES_LOCK_BUTTON)}
              mainAction={<EngineLockButton />}
              recurringAction={
                <RecurringMethodButton
                  actions={[
                    <EngineLockButton
                      asRecurringJob
                      recurringMethod={ToggleType.LOCK}
                    />,
                    <EngineLockButton
                      asRecurringJob
                      recurringMethod={ToggleType.UNLOCK}
                    />,
                  ]}
                />
              }
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              disabled={!hasAuthority(Authority.ENGINES_UPLOAD_PACK_BUTTON)}
              mainAction={<EngineUploadPackButton />}
              recurringAction={<EngineUploadPackButton asRecurringJob />}
            />
          </Grid>
          <Grid item>
            <EditButton
              basePath={`/${ResourceName.ENGINES}`}
              record={engine}
              variant="contained"
              style={{ margin: '2px' }}
              disabled={!hasAuthority(Authority.EDIT_ENGINES)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const SetStateActions = ({ ...props }) => {
  const { record: engine } = props

  return (
    <>
      <Box p={1} display="block" textAlign="right">
        <Grid container direction="row" justify="flex-end" alignItems="center">
          {engine?.hardwareVersion !== HardwareVersions.SPOM4 && (
            <Grid item>
              <EngineForceReportStateButton />
            </Grid>
          )}
          {engine?.hardwareVersion !== HardwareVersions.SPOM4 && (
            <Grid item>
              <EngineResetModulesButton />
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  )
}

export const EngineTabs = ({ ...props }) => {
  const { simpleView, createdAt, ...rest } = props
  const { record: engine } = rest
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()

  const { data: serverDate } = useQuery(
    {
      type: sanitizeFetchType('getServerDateTime'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: !simpleView && !createdAt,
    },
  )

  const eventsFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="name" alwaysOn />],
      resource: ResourceName.ENGINES,
    })

  const entrancesFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="note" alwaysOn />],
      resource: ResourceName.ENGINES,
    })

  return (
    <TabbedShowLayout
      {...rest}
      syncWithLocation={!simpleView}
      tabs={
        <TabbedShowLayoutTabs
          variant="scrollable"
          scrollButtons="auto"
          {...rest}
        />
      }
    >
      <Tab label="resources.engines.tabs.general">
        <TextField label="ID" source="id" />
        <TextField source="name" />
        <FunctionField<EngineDto>
          source="isWatched"
          render={(record?: EngineDto) => (
            <div>
              {record?.isWatched
                ? translate('const.yes')
                : translate('const.no')}
            </div>
          )}
        />
        <DateField
          source="lastRefresh"
          showTime
          options={{
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }}
        />
        <FunctionField<EngineDto>
          source="direction"
          render={(record?: EngineDto) => (
            <div>
              {translate(
                `resources.enums.deviceDirection.${record?.direction}`,
              )}
            </div>
          )}
        />
        {engine?.categoryId && hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.DEVICE_CATEGORIES}
            link="show"
          >
            <FunctionField<DeviceCategoryExtendedDto>
              source="hierarchyString"
              render={(record?: DeviceCategoryExtendedDto) =>
                `${record?.id}. ${record?.hierarchyString}`
              }
            />
          </ReferenceField>
        )}
        {engine?.cameraId && hasAuthority(Authority.VIEW_CAMERAS) && (
          <ReferenceField
            source="cameraId"
            reference={ResourceName.CAMERAS}
            link="show"
          >
            <FunctionField<CameraDto>
              source="name"
              render={(camera?: CameraDto) =>
                `${camera?.id}. ${camera?.description}`
              }
            />
          </ReferenceField>
        )}
        {engine?.hardwareVersion === HardwareVersions.SPOM4 && (
          <SPOM4FullAddressField source="tsCommAddress" />
        )}
        <FunctionField<EngineDto>
          source="stanbyMode"
          render={(record?: EngineDto) => (
            <div>
              {translate(`resources.enums.standbyMode.${record?.stanbyMode}`)}
            </div>
          )}
        />
        {engine?.disabledId && (
          <ReferenceField
            source="disabledId"
            label="resources.engines.fields.disabled"
            reference={ResourceName.VERIFIER_DEVICE_DISABLED}
            link="show"
          >
            <FunctionField<VerifierDeviceDisabledDto>
              source="name"
              render={(disabled?: VerifierDeviceDisabledDto) =>
                `${disabled?.id}. ${disabled?.name}`
              }
            />
          </ReferenceField>
        )}
        <FunctionField<EngineDto>
          source="onlineInfixCeck"
          render={(record?: EngineDto) => (
            <div>
              {record?.onlineInfixCeck
                ? translate('const.yes')
                : translate('const.no')}
            </div>
          )}
        />
        <FunctionField<EngineDto>
          source="multiplicity"
          render={(record?: EngineDto) => (
            <div>
              {translate(
                `resources.enums.multiplicities.${record?.multiplicity}`,
              )}
            </div>
          )}
        />
        <FunctionField<EngineDto>
          source="hardwareVersion"
          render={(record?: EngineDto) => (
            <div>
              {translate(
                `resources.enums.hardwareVersions.${record?.hardwareVersion}`,
              )}
            </div>
          )}
        />
        {engine?.tsCanId && hasAuthority(Authority.VIEW_TS_CANS) && (
          <ReferenceField
            source="tsCanId"
            reference={ResourceName.TS_CANS}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        {engine?.supervisorId && hasAuthority(Authority.VIEW_PALMS) && (
          <ReferenceField
            source="supervisorId"
            reference={ResourceName.PALMS}
            link="show"
          >
            <FunctionField
              source="name"
              render={(palm) => `${palm?.id}. ${palm?.name}`}
            />
          </ReferenceField>
        )}
        {(!simpleView && <GeneralActions record={engine} />) || (
          <BlankActions />
        )}
      </Tab>
      {hasAuthority(Authority.VIEW_EVENTS) && !simpleView && (
        <Tab path="events" label={translate('resources.engines.tabs.events')}>
          <>
            <RelationTab<EngineDto>
              resource={ResourceName.EVENTS}
              source="eventsIds"
              mode={hasAuthority(Authority.EDIT_ENGINES) ? 'edit' : 'show'}
              attachMethod="attachEvents"
              detachMethod="detachEvents"
              attachRequestPayload={(r, ids) => ({
                engineId: r.id,
                eventsIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                engineId: r.id,
                eventsIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterVerifierDeviceId === true
              }
              filters={eventsFilters() as ReactElement[]}
              filterDefaultValues={{
                verifierDeviceId: engine?.id,
                filterVerifierDeviceId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField label="ID" source="id" />
              <TextField source="name" />
            </RelationTab>
          </>
        </Tab>
      )}
      {hasAuthority(Authority.VIEW_ENTRANCES) && !simpleView && (
        <Tab
          path="entrances"
          label={translate('resources.engines.tabs.entrances')}
        >
          <>
            <RelationTab<EngineDto>
              resource={ResourceName.ENTRANCES}
              source="entrancesIds"
              mode={hasAuthority(Authority.EDIT_ENGINES) ? 'edit' : 'show'}
              attachMethod="attachEntrances"
              detachMethod="detachEntrances"
              attachRequestPayload={(r, ids) => ({
                engineId: r.id,
                entrancesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                engineId: r.id,
                entrancesIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterVerifierDeviceId === true
              }
              filters={entrancesFilters() as ReactElement[]}
              filterDefaultValues={{
                verifierDeviceId: engine?.id,
                filterVerifierDeviceId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField label="ID" source="id" />
              <TextField source="note" />
            </RelationTab>
          </>
        </Tab>
      )}
      {engine?.jsonStatus && (
        <Tab label="resources.engines.tabs.statuses" path="statuses">
          <DeviceStatusesTab
            jsonStatus={engine.jsonStatus}
            deviceType={DeviceTypes.ENGINE}
            referenceDateTime={createdAt || serverDate?.serverDateTime}
          />
          {(!simpleView && <SetStateActions />) || <BlankActions />}
        </Tab>
      )}
    </TabbedShowLayout>
  )
}

export const EngineShow = (props: ShowProps) => {
  const classes = useStyles()

  const sidebarOpen = useSelector(
    (state: ReduxState) => state.admin.ui.sidebarOpen,
  )

  const theme = useTheme()
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))

  return (
    <Show
      {...props}
      actions={false}
      title={<ShowTitle />}
      className={
        !xSmallScreen
          ? classNames(classes.show, sidebarOpen && classes.showSideOpen)
          : classes.showXSmall
      }
    >
      <EngineTabs />
    </Show>
  )
}
